import { useState, useEffect, useMemo } from 'react';

export default function OrderPanel({ onPlaceOrder, currentPrice, spread, equity, playbooks = [], onPlaybookChange }) {
  const [direction, setDirection] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [price, setPrice] = useState('');
  const [volume, setVolume] = useState('1');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [riskPercent, setRiskPercent] = useState('1');
  const [autoSize, setAutoSize] = useState(false);
  const [playbookId, setPlaybookId] = useState('');
  const [trailingStop, setTrailingStop] = useState('');

  // Auto position sizing
  const autoVolume = useMemo(() => {
    if (!autoSize || !equity || !currentPrice) return null;
    const sl = parseFloat(stopLoss);
    const rp = parseFloat(riskPercent);
    if (!sl || !rp || rp <= 0) return null;
    const slDistance = Math.abs(currentPrice - sl);
    if (slDistance <= 0) return null;
    const riskAmount = equity * (rp / 100);
    return Math.round((riskAmount / slDistance) * 100) / 100;
  }, [autoSize, equity, currentPrice, stopLoss, riskPercent]);

  useEffect(() => {
    if (autoSize && autoVolume != null && autoVolume > 0) {
      setVolume(String(autoVolume));
    }
  }, [autoSize, autoVolume]);

  const riskAmount = useMemo(() => {
    if (!equity) return null;
    const rp = parseFloat(riskPercent);
    if (!rp) return null;
    return Math.round(equity * (rp / 100) * 100) / 100;
  }, [equity, riskPercent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const vol = parseFloat(volume);
    if (!vol || vol <= 0) return;
    const sl = stopLoss ? parseFloat(stopLoss) : null;
    const tp = takeProfit ? parseFloat(takeProfit) : null;
    const pb = playbookId ? parseInt(playbookId) : null;

    const trail = trailingStop ? parseFloat(trailingStop) : null;
    if (orderType === 'market') {
      onPlaceOrder({ type: 'market', direction, volume: vol, stopLoss: sl, takeProfit: tp, playbookId: pb, trailingDistance: trail });
    } else {
      const p = parseFloat(price);
      if (!p || p <= 0) return;
      onPlaceOrder({ type: orderType, direction, price: p, volume: vol, stopLoss: sl, takeProfit: tp, playbookId: pb, trailingDistance: trail });
    }
    setPrice('');
    setStopLoss('');
    setTakeProfit('');
  };

  const displayPrice = currentPrice ? currentPrice.toFixed(5) : '—';

  return (
    <div className="order-panel">
      {/* Buy/Sell buttons */}
      <div className="order-direction">
        <button type="button" className={`order-dir-btn order-dir-buy ${direction === 'buy' ? 'active' : ''}`}
          onClick={() => setDirection('buy')}>BUY</button>
        <button type="button" className={`order-dir-btn order-dir-sell ${direction === 'sell' ? 'active' : ''}`}
          onClick={() => setDirection('sell')}>SELL</button>
      </div>

      {/* Order type tabs */}
      <div className="order-type-tabs">
        {['market', 'limit', 'stop'].map((t) => (
          <button key={t} type="button" className={`order-type-tab ${orderType === t ? 'active' : ''}`}
            onClick={() => setOrderType(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <form className="order-form" onSubmit={handleSubmit}>
        {/* Price */}
        {orderType === 'market' ? (
          <div className="order-field">
            <label>Market Price</label>
            <div className="order-market-info">
              <span className="order-market-price">{displayPrice}</span>
              {spread > 0 && <span className="order-spread">spread {spread}</span>}
            </div>
          </div>
        ) : (
          <div className="order-field">
            <label>Price</label>
            <input type="number" step="any" value={price}
              onChange={(e) => setPrice(e.target.value)} placeholder={displayPrice} />
          </div>
        )}

        {/* Volume + Auto Size */}
        <div className="order-field-row">
          <div className="order-field" style={{ flex: 1 }}>
            <label>Volume {autoSize && autoVolume ? '(auto)' : ''}</label>
            <input type="number" step="0.01" min="0.01" value={volume}
              onChange={(e) => setVolume(e.target.value)}
              className={autoSize ? 'auto-sized' : ''} />
          </div>
          <div className="order-field" style={{ flex: 1 }}>
            <label>
              <input type="checkbox" checked={autoSize} onChange={e => setAutoSize(e.target.checked)}
                style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Risk %
            </label>
            <input type="number" step="0.1" min="0.1" max="100" value={riskPercent}
              onChange={(e) => setRiskPercent(e.target.value)}
              disabled={!autoSize} />
          </div>
        </div>
        {autoSize && riskAmount != null && (
          <div className="order-risk-info">
            Risking ${riskAmount} ({riskPercent}% of equity)
            {!parseFloat(stopLoss) && <span className="order-risk-warn"> — set SL to auto-size</span>}
          </div>
        )}

        <div className="order-field-row">
          <div className="order-field">
            <label>Stop Loss</label>
            <input type="number" step="any" value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)} placeholder="—" />
          </div>
          <div className="order-field">
            <label>Take Profit</label>
            <input type="number" step="any" value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)} placeholder="—" />
          </div>
        </div>

        {/* Trailing Stop */}
        <div className="order-field">
          <label>Trailing Stop (distance)</label>
          <input type="number" step="any" min="0" value={trailingStop}
            onChange={(e) => setTrailingStop(e.target.value)} placeholder="Off" />
        </div>

        {/* Playbook selector */}
        {playbooks.length > 0 && (
          <div className="order-field">
            <label>Playbook</label>
            <select value={playbookId} onChange={e => {
              setPlaybookId(e.target.value);
              if (onPlaybookChange) onPlaybookChange(e.target.value ? parseInt(e.target.value) : null);
            }}>
              <option value="">None</option>
              {playbooks.map(pb => (
                <option key={pb.id} value={pb.id}>{pb.name}</option>
              ))}
            </select>
          </div>
        )}

        <button type="submit" className={`order-submit-btn order-submit-${direction}`}>
          {orderType === 'market'
            ? `${direction.toUpperCase()} Market`
            : `Place ${orderType.charAt(0).toUpperCase() + orderType.slice(1)}`}
        </button>
      </form>
    </div>
  );
}
